/**
 * O miolo da conversa do Assistente: perguntas (balão à direita), respostas
 * (cartão com Markdown, fontes, Copiar e Responder), o "consultando…" e a
 * falha com "Tentar de novo". Sem turnos, o começo com as sugestões.
 *
 * Turno: `{id, papel: "pessoa" | "assistente", texto, fontes, em, citada}`;
 * `citada` é a resposta que a pergunta responde (`{id, papel, texto}`).
 */
import React, { useEffect, useState } from "react";
import { FiCheck, FiCopy, FiCornerUpLeft } from "react-icons/fi";
import styled, { keyframes } from "styled-components";

import { useToast } from "../../../context/ToastContext";
import { horaCurta } from "../apresentacao";
import { BotaoDeTexto, CELULAR } from "../conversas/estilos";
import { copiarResposta, textoParaCopiar } from "../copiaDaResposta";
import IconeDaIA from "../IconeDaIA";
import Markdown from "../Markdown";
import { Balao } from "../Mensagens";

const SUGESTOES = Object.freeze([
  "O que está atrasado hoje?",
  "O que cada projetista tem na mão?",
  "Quem está esperando resposta da equipe?",
  "Resumo do dia",
]);
const NOMES_DAS_FONTES = Object.freeze({
  listar_clientes: "lista de clientes",
  ficha_cliente: "ficha do cliente",
  listar_pendencias: "pendências",
  mensagens_recentes: "mensagens do WhatsApp",
  historico_arquivado: "histórico arquivado",
  divergencias: "divergências",
  prazos: "prazos",
  resumo_do_dia: "resumo do dia",
  status_monitor: "estado do monitor",
  priorizar_com_jev: "julgamento do Jev",
  buscar_nas_conversas: "busca no WhatsApp",
  carga_por_projetista: "carga por projetista",
  conversas_sem_resposta: "conversas sem resposta",
  memoria: "conversas anteriores",
});
/** Tempo do "copiada" (ícone de visto) depois de copiar. */
const TEMPO_DO_COPIADO_MS = 1600;
const AUTOR_DA_CITACAO = "Assistente";

/** Largura de leitura da conversa (a caixa de escrever usa a mesma). */
export const Coluna = styled.div`
  width: 100%;
  max-width: 780px;
  margin: 0 auto;
  min-width: 0;
`;

const ListaDeTurnos = styled(Coluna)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0 24px;
`;

const Pergunta = styled.div`
  align-self: flex-end;
  max-width: min(85%, 560px);
`;

const Resposta = styled.article`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  min-width: 0;
`;

const MarcaDaIA = styled.span`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--cp-balao, var(--surface));
  box-shadow: var(--shadow-1);
  color: var(--ink-2);
`;

const CartaoDaResposta = styled.div`
  min-width: 0;
  padding: 12px 16px 10px;
  border-radius: 4px 12px 12px 12px;
  background: var(--cp-balao, var(--surface));
  box-shadow: var(--shadow-1);
`;

const Rodape = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--divider);
  font-size: 12px;
  color: var(--ink-3);

  time {
    font-variant-numeric: tabular-nums;
  }
`;

const FimDoRodape = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;

  time {
    margin-left: 6px;
  }
`;

/* Copiar e Responder: só ícone, discretos até o mouse ou o foco chegar. */
const AcaoDaResposta = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
  transition: background-color 120ms ease-out, color 120ms ease-out;

  &:hover {
    background: var(--surface-2);
    color: var(--ink);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 0;
  }

  &[data-feito="sim"] {
    color: var(--success-text);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }
  }
`;

const pulsar = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
`;

const Consultando = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  color: var(--ink-2);

  > span {
    display: inline-flex;
    gap: 4px;
  }

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--ink-3);
    animation: ${pulsar} 1.1s ease-in-out infinite;
  }

  i:nth-child(2) {
    animation-delay: 0.15s;
  }

  i:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    i {
      animation: none;
      opacity: 0.6;
    }
  }
`;

const Erro = styled.p`
  margin: 0 0 0 38px;
  font-size: 13px;
  color: var(--danger-text);
`;

const Inicio = styled(Coluna)`
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: clamp(32px, 10vh, 96px) 0 24px;
  text-align: center;

  h2 {
    margin: 8px 0 0;
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--ink);
    text-wrap: balance;
  }

  p {
    margin: 0;
    max-width: 46ch;
    font-size: 14px;
    line-height: 1.55;
    color: var(--ink-2);
    text-wrap: pretty;
  }
`;

const MarcaGrande = styled(MarcaDaIA)`
  width: 52px;
  height: 52px;
  border-radius: 14px;
`;

const Sugestoes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  max-width: 560px;
  margin-top: 18px;

  @media ${CELULAR} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Sugestao = styled.button`
  min-height: 44px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--cp-balao, var(--surface));
  color: var(--ink);
  font: inherit;
  font-size: 13.5px;
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease-out, transform 120ms ease-out;

  &:hover {
    border-color: var(--border-strong);
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }
  }
`;

function nomesDasFontes(fontes) {
  return [...new Set(fontes || [])].map((f) => NOMES_DAS_FONTES[f] || f).join(" · ");
}

/** A citação como mensagem para o `Balao` e a faixa da escrita: autor "Assistente" e o texto sem marcas. */
export function mensagemDaCitacao(citada) {
  return { autor: AUTOR_DA_CITACAO, texto: textoParaCopiar(citada.texto) };
}

function BotaoCopiar({ texto }) {
  const toast = useToast();
  const [copiada, setCopiada] = useState(false);
  useEffect(() => {
    if (!copiada) return undefined;
    const relogio = setTimeout(() => setCopiada(false), TEMPO_DO_COPIADO_MS);
    return () => clearTimeout(relogio);
  }, [copiada]);

  async function copiar() {
    if (await copiarResposta(texto)) setCopiada(true);
    else toast.error("Não deu para copiar. Selecione o texto e use Ctrl+C.");
  }

  const rotulo = copiada ? "Resposta copiada" : "Copiar resposta";
  return (
    <AcaoDaResposta type="button" onClick={copiar} aria-label={rotulo} title={rotulo} data-feito={copiada ? "sim" : undefined}>
      {copiada ? <FiCheck size={15} aria-hidden="true" /> : <FiCopy size={15} aria-hidden="true" />}
    </AcaoDaResposta>
  );
}

function TurnoDaResposta({ turno, aoResponder }) {
  const fontes = nomesDasFontes(turno.fontes);
  return (
    <Resposta>
      <MarcaDaIA aria-hidden="true"><IconeDaIA /></MarcaDaIA>
      <CartaoDaResposta>
        <Markdown texto={turno.texto} />
        <Rodape>
          <span>{fontes ? `Fontes: ${fontes}` : "Sem consulta às fontes"}</span>
          <FimDoRodape>
            <BotaoCopiar texto={turno.texto} />
            <AcaoDaResposta
              type="button"
              onClick={() => aoResponder(turno)}
              aria-label="Responder a esta resposta"
              title="Responder a esta resposta (a pergunta vai citando ela)"
            >
              <FiCornerUpLeft size={15} aria-hidden="true" />
            </AcaoDaResposta>
            <time dateTime={turno.em}>{horaCurta(turno.em)}</time>
          </FimDoRodape>
        </Rodape>
      </CartaoDaResposta>
    </Resposta>
  );
}

export function ComecoDaConversa({ aoPerguntar, desabilitado }) {
  return (
    <Inicio>
      <MarcaGrande aria-hidden="true"><IconeDaIA tamanho={24} /></MarcaGrande>
      <h2>Pergunte sobre os projetos</h2>
      <p>
        O assistente consulta a planilha, as conversas do WhatsApp e as análises da Central. Só consulta: não envia nada e não
        muda nada. As conversas ficam salvas aqui, só para você.
      </p>
      <Sugestoes aria-label="Perguntas sugeridas">
        {SUGESTOES.map((s) => (
          <Sugestao key={s} type="button" disabled={desabilitado} onClick={() => aoPerguntar(s)}>{s}</Sugestao>
        ))}
      </Sugestoes>
    </Inicio>
  );
}

export default function Turnos({ turnos, enviando, falha, aoTentarDeNovo, aoResponder }) {
  return (
    <ListaDeTurnos>
      {turnos.map((t) =>
        t.papel === "pessoa" ? (
          <Pergunta key={t.id}>
            <Balao
              mensagem={{ id: t.id, de_mim: true, texto: t.texto, enviada_em: t.em }}
              citada={t.citada ? mensagemDaCitacao(t.citada) : null}
            />
          </Pergunta>
        ) : (
          <TurnoDaResposta key={t.id} turno={t} aoResponder={aoResponder} />
        ),
      )}
      {enviando ? (
        <Resposta>
          <MarcaDaIA aria-hidden="true"><IconeDaIA /></MarcaDaIA>
          <CartaoDaResposta>
            <Consultando role="status">
              <span aria-hidden="true"><i /><i /><i /></span>
              Consultando a planilha e as conversas…
            </Consultando>
          </CartaoDaResposta>
        </Resposta>
      ) : null}
      {falha && !enviando ? (
        <Erro role="alert">
          {falha.mensagem}{" "}
          <BotaoDeTexto type="button" onClick={aoTentarDeNovo}>Tentar de novo</BotaoDeTexto>
        </Erro>
      ) : null}
    </ListaDeTurnos>
  );
}
